import { config } from "@/config/environment";
import { isPWA } from "@/utilities/pwa-utils";

export type PwaInstalledDetail = {
  event: "pwa_installed";
  installedAt: string;
  userAgent: string;
  displayModeStandalone: boolean;
};

/**
 * Fires after the browser finishes installing the PWA (`appinstalled`).
 * - Dispatches `window` event `tempii:pwa-installed` for custom listeners / GTM.
 * - If `config.features.analytics` and `config.app.pwaInstallReportUrl` are set, POSTs JSON via `sendBeacon` (fallback `fetch` with `keepalive`).
 */
const SESSION_KEY = "tempii:pwa-install-reported";

export function reportPwaInstalled(): void {
  if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(SESSION_KEY)) {
    return;
  }

  const detail: PwaInstalledDetail = {
    event: "pwa_installed",
    installedAt: new Date().toISOString(),
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    displayModeStandalone: isPWA()
  };

  window.dispatchEvent(new CustomEvent("tempii:pwa-installed", { detail }));

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(SESSION_KEY, "1");
  }

  const w = window as Window & { dataLayer?: unknown[] };
  if (Array.isArray(w.dataLayer)) {
    w.dataLayer.push({ detail });
  }

  if (!config.features.analytics) {
    return;
  }

  const url = config.app.pwaInstallReportUrl?.trim();
  if (!url) {
    return;
  }

  const body = JSON.stringify(detail);

  try {
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        url,
        new Blob([body], { type: "application/json" })
      );
      if (ok) {
        return;
      }
    }
  } catch {
    /* fall through to fetch */
  }

  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    mode: "cors"
  }).catch(() => {
    /* non-blocking */
  });
}
